import React, { useEffect, useState } from "react";
import {
  Controller,
  FieldError,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { workOutCreateInterface } from "../../api/types/types";
import {
  useGetWorkOutById,
  useUpdateWorkout,
} from "../../api/useProducts/useWorkOut";
import {
  CustomButton,
  FileUploadIcon,
  InputField,
  Sidebar,
} from "../../components";
import { CategoryOption } from "../../config/Category";
import Loader from "../../components/Loader";
import useSingleFileUpload from "../../hook/useSingleFileUpload";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import { useQueryClient } from "@tanstack/react-query";

const UpdateWorkOuts: React.FC = () => {
  const { id } = useParams();
  const { WorkoutData, WorkoutLoading, WorkoutRefetch } = useGetWorkOutById(
    Number(id)
  );
  const { mutate, isPending } = useUpdateWorkout(Number(id));
  const { uploadImage, isUploading } = useSingleFileUpload();
  const query = useQueryClient();

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    control,
  } = useForm<workOutCreateInterface>();

  const [uploadedData, setUploadedData] = useState<File>();

  const onSubmit: SubmitHandler<workOutCreateInterface> = async (data) => {
    let imageUrl;
    if (uploadedData) {
      imageUrl = await uploadImage(uploadedData);
    }
    mutate(
      {
        ...(data?.title && { title: data?.title }),
        ...(data?.calories && { calories: data?.calories }),
        ...(data?.category && { category: data?.category }),
        // ...(data?.points && { points: data?.points }),
        ...(data?.description &&
          data?.description !== "<p><br></p>" && {
            description: data?.description,
          }),
        ...(data?.level && { level: data?.level }),
        ...(uploadedData && {
          media: imageUrl,
        }),
      },
      {
        onSuccess: async (data) => {
          toast.success("successfully created");
          await query.resetQueries({
            queryKey: ["getWorkoutData"],
            exact: true,
          });
          navigate(`/workouts/exercises/update/${id}`);
        },
        onError: (error: any) => {
          toast.error(error.response.data.message);
        },
      }
    );
  };

  const optionEndurance = [
    { value: "Beginner", label: "Beginner" },
    { value: "Intermediate", label: "Intermediate" },
    { value: "Advanced", label: "Advanced" },
  ];

  useEffect(() => {
    WorkoutRefetch();
  }, [id]);
  useEffect(() => {
    if (WorkoutData) {
      setValue("calories", `${WorkoutData?.calories}`);
      setValue("category", `${WorkoutData?.category}`);
      setValue("description", `${WorkoutData?.description}`);
      setValue("level", `${WorkoutData?.level}`);
      // setValue("points", Number(WorkoutData?.points));
      setValue("title", `${WorkoutData?.title}`);
    }
  }, [id, WorkoutData]);
  const breadcrumbItems = [
    { title: "Workouts", link: "workouts" },
    { title: "Edit " },
  ];
  return (
    <Sidebar>
      <div className="mt-3">
        <CustomBreadcrumb items={breadcrumbItems} />
      </div>
      {WorkoutLoading ? (
        <Loader />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-3 mt-6">
            <InputField
              label="Title"
              placeholder="Enter title here"
              type="text"
              // name="workoutTitle"
              register={register("title", {
                required: "Title is required",
                minLength: {
                  value: 3,
                  message: "Title must be at least 3 characters long",
                },
              })}
              error={errors.title?.message}
            />

            <div>
              <p className="block text-gray-700 mb-2">Category</p>
              <Controller
                name="category"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={CategoryOption}
                    value={CategoryOption.find(
                      (option) => option.value === field.value
                    )}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value)
                    }
                    placeholder="Select category"
                    styles={{
                      control: (base) => ({
                        ...base,
                        height: "44px", // Background color for the select control
                        boxShadow: "none",
                        caretColor: "#24B1B3",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "&:focus": {
                          outline: "none !important",
                          boxShadow: "none !important",
                          borderColor: "gray !important",
                        },
                        "&:hover": { borderColor: "none !important" },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "rgb(148 163 184)", // Set placeholder color here
                      }),
                    }}
                  />
                )}
              />
              {errors.category && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.category.message}
                </p>
              )}
            </div>

            <InputField
              label="Calories"
              placeholder="Enter calories"
              type="number"
              // name="calories"
              register={register("calories", {
                required: "Calories is required",
                validate: (value) => {
                  // @ts-ignore
                  if (value <= 0) {
                    return "calories must be a positive number";
                  }
                  // @ts-ignore
                  if (!Number.isInteger(parseFloat(value))) {
                    return "calories must be a whole number";
                  }
                  return true;
                },
              })}
              error={errors.calories?.message}
            />

            {/* <InputField
              label="Points"
              placeholder="Enter points"
              type="number"
              // name="points"
              register={register("points", {
                required: "Points are required",
                validate: (value) => {
                  // @ts-ignore
                  if (value <= 0) {
                    return "points must be a positive number";
                  }
                  // @ts-ignore
                  if (!Number.isInteger(parseFloat(value))) {
                    return "points must be a whole number";
                  }
                  return true;
                },
              })}
              error={errors.points?.message}
            /> */}
            <div>
              <p className="block text-gray-700 mb-2">Level</p>
              <Controller
                name={`level`}
                control={control}
                rules={{ required: "Level is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={optionEndurance}
                    value={optionEndurance.find(
                      (option) => option.value === field.value
                    )}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value)
                    }
                    placeholder="Select level"
                    styles={{
                      control: (base) => ({
                        ...base,
                        height: "44px", // Background color for the select control
                        boxShadow: "none",
                        caretColor: "#24B1B3",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "&:focus": {
                          outline: "none !important",
                          boxShadow: "none !important",
                          borderColor: "gray !important",
                        },
                        "&:hover": { borderColor: "none !important" },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "rgb(148 163 184)", // Set placeholder color here
                      }),
                    }}
                  />
                )}
              />
              {errors && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.level?.message as string}
                </p>
              )}
            </div>
          </div>
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-3">
            <div className="">
              <p className="block text-gray-700 mb-2">Description</p>
              <Controller
                name="description"
                control={control}
                rules={{ required: "description is required" }}
                render={({ field }) => (
                  <ReactQuill
                    {...field}
                    // onChange={(value) => {
                    //   field.onChange(value);
                    // }}
                    className="quill-editor"
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        ["blockquote", "code-block"],
                        [{ header: 1 }, { header: 2 }],
                        [{ list: "ordered" }, { list: "bullet" }],

                        [{ size: ["small", false, "large", "huge"] }],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ color: [] }, { background: [] }],

                        [{ align: [] }],
                        ["clean"],
                      ],
                    }}
                  />
                )}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.description.message}
                </p>
              )}
            </div>
            <div>
              <p className="block text-gray-700 mb-2">Upload Image</p>

              <div className="mt-3">
                <label htmlFor="file">
                  <div
                    role="button"
                    className="flex flex-col justify-center items-center min-h-52 border-[1.5px] border-dashed border-gray-400 rounded-2xl p-3"
                  >
                    {uploadedData ? (
                      // Display uploaded URL if present
                      <p className="text-center text-sm text-gray-600">
                        {uploadedData.name}
                      </p>
                    ) : (
                      <div>
                        {WorkoutData?.media ? (
                          <p className="text-center text-sm text-gray-600 break-all		text-ellipsis overflow-hidden">
                            {WorkoutData.media}
                          </p>
                        ) : (
                          <>
                            <FileUploadIcon />
                            <CustomButton
                              btntext="Upload"
                              addclass="mt-3"
                              handleClick={() =>
                                document.getElementById("file")?.click()
                              }
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </label>
                <input
                  type="file"
                  className="hidden"
                  id="file"
                  accept={"image/jpeg, image/png, image/jpg"}
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      setUploadedData(e.target.files[0]);
                    }
                  }}
                />
              </div>
              {errors.media && (
                <p className="text-red-500 text-sm mt-1">
                  {(errors.media as FieldError)?.message}
                </p>
              )}
            </div>
          </div>
          <CustomButton
            type="submit"
            btntext="Submit"
            isLoading={isPending || isUploading}
            addclass="mt-9 mx-auto w-1/2"
          />
        </form>
      )}
      {/* <FileUploadModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        onFileUpload={handleFileUpload}
      />{" "} */}
    </Sidebar>
  );
};

export default UpdateWorkOuts;
